/*
 * factory-bundle.js: the "Bundle & save" block (store factory, Horizon 4.2+). Loaded as a module by
 * snippets/factory-bundle.liquid; the browser runs it once.
 *
 * - Rows and first totals are printed in Liquid, so nothing moves when this runs. This file recalculates the
 *   summary when a checkbox or a variant select changes, follows the buy box variant (Horizon re-renders the
 *   section on every variant pick), and swaps in Search & Discovery pairs when custom.pairs is empty.
 * - "Add N to cart" sends ONE /cart/add.js request through factory-kit's addToCart, which announces it with
 *   @shopify/events like Horizon's own product form: the drawer refreshes and opens, the bubble updates.
 * - Tiers come from Theme settings > Factory: cart > Bundle tiers (data-tiers="2:10,3:15"), the same numbers
 *   as the drawer bar. Saving per line = round(price * percent / 100), like a percentage automatic discount.
 * - No colors or fonts here: the block reads --factory-* and --factory-bundle-* custom properties.
 */
const PRODUCT_SELECT = 'shopify:product:select';

/** Money in the shopper's currency, built from a Liquid sample (123456 | money), so markets keep their symbol and separators. */
function moneyFormatter(sample) {
  const text = String(sample || '');
  const found = text.match(/\d[\d.,'’\s  ]*\d/);
  if (!found) return (cents) => (Math.round(cents) / 100).toFixed(2);
  const run = found[0];
  const before = text.slice(0, found.index);
  const after = text.slice(found.index + run.length);
  const tail = run.match(/(\D)(\d{2})$/);
  const decimal = run.replace(/\D/g, '') === '123456' && tail ? tail[1] : '';
  const whole = decimal ? run.slice(0, -3) : run;
  const group = (whole.match(/\D/) || [''])[0];
  const groups = (digits) => digits.replace(/\B(?=(\d{3})+(?!\d))/g, group);
  return (cents) => {
    const value = Math.max(0, Math.round(Number(cents) || 0));
    if (!decimal) return before + groups(String(Math.round(value / 100))) + after;
    const minor = value % 100;
    const body = groups(String(Math.floor(value / 100))) + (minor ? decimal + String(minor).padStart(2, '0') : '');
    return before + body + after;
  };
}

const escapeHTML = (s) =>
  String(s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);

/** Replaces [token] in an escaped template; values in html are inserted as markup. */
function fill(template, values, html = {}) {
  let out = escapeHTML(template || '');
  for (const [key, value] of Object.entries(values)) out = out.split(`[${key}]`).join(escapeHTML(value));
  for (const [key, value] of Object.entries(html)) out = out.split(`[${key}]`).join(value);
  return out;
}

/** Same as fill, for textContent. */
function fillText(template, values) {
  let out = String(template || '');
  for (const [key, value] of Object.entries(values)) out = out.split(`[${key}]`).join(String(value));
  return out;
}

const parseTiers = (raw) =>
  String(raw || '')
    .split(',')
    .map((pair) => pair.split(':').map(Number))
    .filter(([qty, pct]) => qty > 1 && pct > 0)
    .map(([qty, pct]) => ({ qty, pct }))
    .sort((a, b) => a.qty - b.qty);

class FactoryBundle extends HTMLElement {
  connectedCallback() {
    if (this._ready) return;
    this._ready = true;
    this.tiers = parseTiers(this.dataset.tiers);
    this.money = moneyFormatter(this.dataset.money);
    this.section = this.closest('.shopify-section, dialog');
    this.addEventListener('change', this.onChange);
    this.addEventListener('click', this.onClick);
    this.section?.addEventListener(PRODUCT_SELECT, this.onProductSelect);
    if (this.dataset.recsUrl) this.loadRecs();
    this.update();
  }

  disconnectedCallback() {
    this._ready = false;
    this.removeEventListener('change', this.onChange);
    this.removeEventListener('click', this.onClick);
    this.section?.removeEventListener(PRODUCT_SELECT, this.onProductSelect);
  }

  get rows() {
    return Array.from(this.querySelectorAll('[data-factory-bundle-row]'));
  }

  get picked() {
    return this.rows.filter((row) => row.querySelector('[data-factory-bundle-check]')?.checked);
  }

  onChange = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    if (target.matches('[data-factory-bundle-opt]')) this.pickOptions(target);
    if (target.matches('[data-factory-bundle-select]')) this.pickVariant(target);
    if (target.matches('[data-factory-bundle-check], [data-factory-bundle-select], [data-factory-bundle-opt]')) {
      this.touched = true;
      this.update();
    }
  };

  /** One select per option (colour, size): find the variant, or the nearest available one that keeps the value just picked. */
  pickOptions(changed) {
    const row = changed.closest('[data-factory-bundle-row]');
    const master = row?.querySelector('[data-factory-bundle-select]');
    if (!master) return;
    const selects = Array.from(row.querySelectorAll('[data-factory-bundle-opt]'));
    const want = selects.map((s) => s.value);
    const k = Number(changed.dataset.factoryBundleOpt) || 0;
    const variants = Array.from(master.options).map((o) => ({ o, vals: (o.dataset.options || '').split('|') }));
    const open = variants.filter((x) => !x.o.disabled);
    const common = (x) => x.vals.filter((v, i) => v === want[i]).length;
    let match = open.find((x) => common(x) === want.length);
    if (!match) match = open.filter((x) => x.vals[k] === want[k]).sort((a, b) => common(b) - common(a))[0];
    if (!match) match = variants.find((x) => x.o.selected);
    if (!match) return;
    selects.forEach((s, i) => {
      if (match.vals[i] !== undefined) s.value = match.vals[i];
    });
    master.value = match.o.value;
    this.pickVariant(master);
  }

  pickVariant(select) {
    const row = select.closest('[data-factory-bundle-row]');
    const option = select.selectedOptions[0];
    if (!row || !option) return;
    row.dataset.variantId = option.value;
    row.dataset.price = option.dataset.price || row.dataset.price;
    row.dataset.available = option.disabled ? 'false' : 'true';
    const price = row.querySelector('[data-factory-bundle-price]');
    if (price) price.textContent = this.money(row.dataset.price);
    const media = row.querySelector('.factory-bundle__media');
    const img = media?.querySelector('img');
    if (img && option.dataset.image) {
      img.removeAttribute('srcset');
      img.src = option.dataset.image;
    }
    if (media instanceof HTMLAnchorElement && option.dataset.url) media.href = option.dataset.url;
  }

  update() {
    const d = this.dataset;
    const rows = this.rows;
    const picked = rows.filter((row) => row.querySelector('[data-factory-bundle-check]')?.checked);
    rows.forEach((row) => row.toggleAttribute('data-off', !picked.includes(row)));
    const count = picked.length;
    const price = (row) => Number(row.dataset.price) || 0;
    const regular = picked.reduce((sum, row) => sum + price(row), 0);
    const tier = [...this.tiers].reverse().find((t) => count >= t.qty);
    const pct = tier ? tier.pct : 0;
    const save = pct ? picked.reduce((sum, row) => sum + Math.round((price(row) * pct) / 100), 0) : 0;
    const word = (n) => (n === 1 ? d.itemOne : d.itemOther);

    const countEl = this.querySelector('[data-fb-count]');
    if (countEl) countEl.innerHTML = fill(d.countText, { count, items: word(count) });
    const was = this.querySelector('[data-fb-was]');
    if (was) {
      was.hidden = save === 0;
      const amount = was.querySelector('[data-fb-was-amount]');
      if (amount) amount.textContent = this.money(regular);
    }
    const now = this.querySelector('[data-fb-now]');
    if (now) now.textContent = this.money(regular - save);

    const saveEl = this.querySelector('[data-fb-save]');
    if (saveEl) {
      const next = this.tiers.find((t) => t.qty > count);
      if (save > 0) {
        saveEl.innerHTML = fill(d.saveText, { percent: `${pct}%` }, { amount: `<b class="factory-num">${escapeHTML(this.money(save))}</b>` });
      } else if (next) {
        const need = next.qty - count;
        saveEl.innerHTML = fill(d.hintText, { count: need, items: word(need), percent: `${next.pct}%` });
      } else {
        saveEl.textContent = '';
      }
    }
    this.querySelectorAll('.factory-bundle__tier[data-qty]').forEach((chip) => {
      chip.classList.toggle('is-on', Boolean(tier) && Number(chip.dataset.qty) === tier.qty);
    });

    const button = this.querySelector('[data-fb-add]');
    if (button) {
      const main = this.querySelector('[data-factory-bundle-main]');
      const soldOut = main && main.dataset.available === 'false';
      button.textContent = soldOut ? d.soldOut : fillText(d.addLabel, { count });
      button.disabled = Boolean(soldOut) || count === 0 || this.busy === true;
    }
  }

  /** The buy box picked another variant: Horizon fetched the section, take this product's row from it. */
  onProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;
    event.promise
      ?.then(({ detail }) => {
        const html = detail && detail.html;
        if (!html || !html.querySelector) return;
        const fresh = html.querySelector(`factory-bundle[data-block-id="${this.dataset.blockId}"] [data-factory-bundle-main]`);
        const main = this.querySelector('[data-factory-bundle-main]');
        if (!fresh || !main) return;
        main.replaceWith(document.importNode(fresh, true));
        this.update();
      })
      .catch(() => {});
  };

  /** No custom.pairs: Search & Discovery complementary products replace the fallback rows, same number of rows. */
  loadRecs() {
    fetch(this.dataset.recsUrl, { credentials: 'same-origin' })
      .then((response) => (response.ok ? response.text() : ''))
      .then((html) => {
        if (!html || this.touched) return;
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const list = this.querySelector('[data-factory-bundle-list]');
        const main = list?.querySelector('[data-factory-bundle-main]');
        if (!list || !main) return;
        const limit = Number(this.dataset.limit) || 2;
        const seen = new Set([main.dataset.productId]);
        const unique = (row) => !seen.has(row.dataset.productId) && seen.add(row.dataset.productId);
        const recs = Array.from(doc.querySelectorAll('[data-factory-bundle-recs] [data-factory-bundle-row]')).filter(unique);
        if (!recs.length) return;
        const current = Array.from(list.querySelectorAll('[data-factory-bundle-row]:not([data-factory-bundle-main])'));
        const keep = current.filter(unique);
        const rows = [...recs.map((row) => this.adopt(row)), ...keep].slice(0, Math.max(limit, 1));
        current.forEach((row) => row.remove());
        list.append(...rows);
        this.hidden = false;
        this.update();
      })
      .catch(() => {});
  }

  /** Rows from the recommendations section use FBX ids; give them this block's ids. */
  adopt(row) {
    const node = document.importNode(row, true);
    const uid = this.dataset.uid || 'FB';
    const rename = (value) => value.split('FBX-').join(`${uid}-`);
    [node, ...node.querySelectorAll('[id], [for], [aria-labelledby], [aria-describedby]')].forEach((el) => {
      ['id', 'for', 'aria-labelledby', 'aria-describedby'].forEach((attr) => {
        if (el.hasAttribute(attr)) el.setAttribute(attr, rename(el.getAttribute(attr)));
      });
    });
    return node;
  }

  onClick = async (event) => {
    const button = event.target instanceof Element ? event.target.closest('[data-fb-add]') : null;
    if (!button || button.disabled || this.busy) return;
    const error = this.querySelector('[data-fb-error]');
    const formId = this.section?.querySelector('product-form-component input[name="id"]')?.value;
    const items = this.picked
      .filter((row) => row.dataset.available !== 'false')
      .map((row) => ({
        id: Number(row.hasAttribute('data-factory-bundle-main') && formId ? formId : row.dataset.variantId),
        quantity: 1,
      }))
      .filter((item) => item.id > 0);
    if (!items.length) return;
    this.busy = true;
    button.setAttribute('aria-busy', 'true');
    button.disabled = true;
    if (error) error.hidden = true;
    let result = { ok: false };
    try {
      const kit = await import(this.dataset.kit);
      result = await kit.addToCart(items, this, 'product');
    } catch (e) {
      result = { ok: false };
    }
    this.busy = false;
    button.removeAttribute('aria-busy');
    if (!result.ok && error) {
      const message = result.message && result.message !== 'Cart request failed' ? result.message : '';
      error.textContent = message || this.dataset.errorText || '';
      error.hidden = !error.textContent;
    }
    this.update();
  };
}

if (!customElements.get('factory-bundle')) customElements.define('factory-bundle', FactoryBundle);
